import { PricingPeriod, usePricing } from '@atlassiansox/bxp-use-pricing';
import { useContext, useMemo } from 'react';
import { FetchPricingContext, IFetchPricing } from '../contexts';
import { useGlobalStore } from '../store';
import { PlanName, ProductPlan } from '../models';

const getNumUnits = (
  numUnits: number,
  planName: PlanName,
  selectedBillingCycle: PricingPeriod,
  unitLimits: [number, number]
) => {
  let adjNumUnits = 0;

  // following scenarios do not have pricing and should send a zero unit count,
  // the zero pricing will not be displayed and is needed only to retain the
  // correct array order of the pricing requests/responses

  // Free plan has no pricing
  const isFree = planName === 'Free';

  // Enterprise plans do not have monthly pricing
  const isMonthlyEnterprise =
    planName === 'Enterprise' && selectedBillingCycle === 'MONTHLY';

  // Enterprise plans have a minimum unit count
  const isUnmetUnitCount = numUnits < unitLimits[0];

  const isOverLimit = numUnits > unitLimits[1];

  if (
    ![isFree, isMonthlyEnterprise, isUnmetUnitCount, isOverLimit].includes(true)
  ) {
    adjNumUnits = numUnits;
  }

  return adjNumUnits;
};

export const usePricingData = (
  ccpOfferingSKU: ProductPlan['ccpOfferingSKU'],
  hamsProductKey: ProductPlan['hamsProductKey'],
  numUnits: number,
  planName: PlanName,
  unitLimits: ProductPlan['unitLimits'],
  noAnnualPlan?: boolean
) => {
  const localeISOCode = document.documentElement.lang;
  const fetchPricing = useContext<IFetchPricing>(FetchPricingContext);
  const [{ selectedBillingCycle }] = useGlobalStore();

  /* We want to set the limits to 0 for products that do not support an Annual plan
    Only if Annual is selected, will we set the price to 0
  */
  const unitLimitLower =
    !noAnnualPlan || selectedBillingCycle !== 'ANNUAL'
      ? unitLimits && unitLimits[0]
      : 0;
  const unitLimitUpper =
    !noAnnualPlan || selectedBillingCycle !== 'ANNUAL'
      ? unitLimits && unitLimits[1]
      : 0;

  const priceCalcReqWithPeriod = useMemo(() => {
    return [
      {
        ccpOfferingSKU,
        hamsProductKey,
        numUnits: getNumUnits(numUnits, planName, selectedBillingCycle, [
          unitLimitLower,
          unitLimitUpper
        ]),
        period: selectedBillingCycle
      }
    ];
  }, [selectedBillingCycle, ccpOfferingSKU, hamsProductKey, numUnits]);

  return usePricing(
    priceCalcReqWithPeriod || [],
    fetchPricing,
    'USD',
    localeISOCode
  );
};
